import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import isEqual from 'lodash.isequal';
import { FC, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Placeholder from 'src/assets/product-placeholder.svg';
import Button from 'src/components/ui/Button/Button';
import { FormCheckbox } from 'src/components/ui/FormCheckbox/FormCheckbox';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import Tooltip from 'src/components/ui/Tooltip/Tooltip';
import UploadFile from 'src/components/ui/UploadFile/UploadFile';
import UploadPicture from 'src/components/ui/UploadPicture/UploadPicture';
import {
  addProduct,
  editProduct,
  getProduct,
  removeProduct,
  setProduct,
} from 'src/store/actions/productActions';
import { Document, Product } from 'src/store/models/Product';
import { useFileSelector } from 'src/utils/useFileSelector';
import {
  useErrorSelector,
  useLoadingSelector,
} from 'src/utils/useLoadingSelector';
import { v4 as uuidv4 } from 'uuid';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  addModal,
  removeFile,
  removeModal,
} from '../../../features/globalSlice';
import { ModalType } from '../../../store/models/Modal';
import classes from './ProductDetails.module.scss';
import ProductDetailsForm, {
  ProductDetailsFormData,
} from './ProductDetailsForm/ProductDetailsForm';

const defaultValues: ProductDetailsFormData = {
  model: '',
  hardware: '',
  firmware: '',
  weight: '',
  dimensions: '',
  thermalOutput: '',
  notes: '',
  imageUrl: '',
  restricted: false,
};

export interface ProductDetailsProps {
  edit?: boolean;
}

const ProductDetails: FC<ProductDetailsProps> = ({ edit = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const imageId = useRef(uuidv4());
  const documentId = useRef(uuidv4());
  const dataFileId = useRef(uuidv4());

  const methods = useForm<ProductDetailsFormData>({
    mode: 'onChange',
    defaultValues,
  });
  const imageUrl = methods?.watch('imageUrl');

  const image = useFileSelector({ id: imageId.current })?.[0];
  const newDocuments = useFileSelector({ id: documentId.current });
  const newDataFiles = useFileSelector({ id: dataFileId.current });
  const addError = useErrorSelector<ProductDetailsFormData>({
    actionType: addProduct.toString(),
  });
  const loading = useLoadingSelector({
    actionType: getProduct.toString(),
  });
  const product = useAppSelector((state) => state.product.productDetails);

  const [documents, setDocuments] = useState<Document[]>([]);
  const [dataFiles, setDataFiles] = useState<Document[]>([]);

  useEffect(() => {
    dispatch(setProduct(null));
    if (edit) {
      dispatch(getProduct({ productId: params?.productId }));
    }
    return () => {
      dispatch(setProduct(null));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (product) {
      methods?.reset(product);
      dispatch(removeFile({ id: documentId.current }));
      dispatch(removeFile({ id: dataFileId.current }));
      dispatch(removeFile({ id: imageId.current }));
      setDocuments(product.documents || []);
      setDataFiles(product.dataFiles || []);
    }
    // eslint-disable-next-line
  }, [product]);

  useEffect(() => {
    methods.setValue('imageUrl', image ? image?.url : product?.imageUrl || '', {
      shouldDirty: true,
    });
    // eslint-disable-next-line
  }, [image]);

  const addProductHandler = () => {
    const product: Product = {
      ...methods.getValues(),
      dataFiles: [...dataFiles, ...newDataFiles],
      documents: [...documents, ...newDocuments],
    };

    dispatch(
      addModal({
        id: '1',
        modalType: ModalType.PRODUCT_PREVIEW,
        title: t('products:preview'),
        loadingAction: addProduct.toString(),
        data: product,
        primaryAction: () => dispatch(addProduct(product)),
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  const editProductHandler = () => {
    dispatch(
      editProduct({
        ...methods.getValues(),
        dataFiles: [...dataFiles, ...newDataFiles],
        documents: [...documents, ...newDocuments],
      })
    );
  };

  const removeProductHandler = () => {
    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        body: t('products:product_will_be_removed', {
          model: methods?.getValues()?.model,
        }),
        modalType: ModalType.WARNING,
        loadingAction: removeProduct.toString(),
        primaryButtonText: t('common:yes'),
        secondaryButtonText: t('common:no'),
        primaryAction: () => {
          dispatch(removeProduct({ productId: product?.id }));
        },
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  const imageComponent = (
    <div
      className={classes.ProductImage}
      key={imageUrl}
      style={{
        backgroundImage: `url(${imageUrl ? `'${imageUrl}'` : Placeholder})`,
      }}
    />
  );

  const showBackButton =
    methods?.formState?.isDirty ||
    !isEqual(documents, product?.documents || []) ||
    !isEqual(dataFiles, product?.dataFiles || []) ||
    newDocuments?.length ||
    newDataFiles?.length;

  return (
    <FormProvider {...methods}>
      <div className={classes.Container}>
        <PageHeader
          title={edit ? t('products:edit_product') : t('products:add_product')}
          showBackButton
          backConfirmationText={showBackButton && t('common:changes_not_saved')}
          endNode={
            <Button
              onClick={!edit ? addProductHandler : editProductHandler}
              disabled={!(methods?.formState?.isValid && showBackButton)}
              className={classes.HeaderButton}
            >
              {edit ? t('common:save_changes') : t('products:preview')}
            </Button>
          }
        />
        <h4>
          {t('common:general_information')}
          <span>{` (${t('common:required').toLowerCase()})`}</span>
        </h4>
        <ProductDetailsForm loading={loading} />
        {!loading && (
          <div className={classes.Checkbox}>
            <FormCheckbox<ProductDetailsFormData>
              name="restricted"
              control={methods?.control}
              LabelCheckboxProps={{
                color: 'primary',
                label: t('products:restrict_access'),
              }}
            />
            <Tooltip tooltipText={t('products:restrict_access_details')}>
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </div>
        )}
        <h4>{t('common:image')}</h4>
        <p>{t('products:only_one_image')}</p>
        <Controller
          name="imageUrl"
          control={methods.control}
          render={() => (
            <UploadPicture
              uploadTooltip={t('products:product_image_format')}
              imageComponent={imageComponent}
              enableRemove={
                !!(image?.url || (product?.imageUrl && image?.url !== null))
              }
              error={addError?.imageUrl}
              loading={loading}
              imageId={imageId.current}
            />
          )}
        />
        <h4>{t('products:documentation')}</h4>
        <p>{t('products:upload_documentation')}</p>
        <UploadFile
          accept=".doc,.docx,.pdf,.xls,.xlsx"
          uploadTooltip={t('products:documentation_format')}
          files={documents}
          setFiles={setDocuments}
          loading={loading}
          fileId={documentId.current}
        />
        <h4>{t('products:fumis_data')}</h4>
        <p>{t('products:upload_fumis_data')}</p>
        <UploadFile
          accept=".prm,.prl,.vsc,.tmr,.val,.hex,.txt"
          uploadTooltip={t('products:fumis_data_format')}
          files={dataFiles}
          setFiles={setDataFiles}
          loading={loading}
          fileId={dataFileId.current}
        />
        {edit && !loading && (
          <Button variant="outlined" onClick={removeProductHandler}>
            {t('products:remove_product')}
          </Button>
        )}
      </div>
    </FormProvider>
  );
};

export default ProductDetails;
